/**
 * 評価ステータスのラベル変換ユーティリティ（表示ラベルの正本）
 *
 * 表記の設計正本: 設計書類/02_評価管理機能/画面設計_評価ステータス表記統一.md
 *
 * 画面・コンポーネント側でラベル文字列を持たず、必ず本ファイルの関数・定数を使用する。
 * 個人表記は「（通過済み段階）（（次アクション）待ち）」形式とし、
 * 先頭の段階名は組織集計（ファネル）の段階名と一致させる。
 */

export type EvaluationStatus =
  | null
  | 'not_started'
  | 'root_defined'
  | 'goal_setting'
  | 'self_evaluated'
  | 'manager_evaluated'
  | 'dept_evaluated'
  | 'r_applied'
  | 'finalized';

export interface StatusLabelInfo {
  label: string;
  color: 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  chipColor: 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  progressStep: number; // getProgressStep と同一スケール（PROGRESS_STEPS のインデックス）
}

export type GoalApprovalStatus =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'rejected'
  | null
  | undefined;

type StatusColor = StatusLabelInfo['color'];

/**
 * 表示の出し分けに使う追加情報
 *
 * いずれも省略可能。取得できない画面では既定の表記へフォールバックする
 * （設計書 §3.4 の対応表と同一）。
 */
export interface StatusLabelContext {
  /** 評価ルートが設定されているか。false のとき評価行なしは「ルート未設定」 */
  hasRoute?: boolean;
  /** 上長評価無効ルート（部門長が上長役を兼務）か。true のとき次アクションは部門長 */
  managerEvalDisabled?: boolean;
  /** 自己評価期間が開始済みか。false のとき目標承認済は「目標確定」 */
  selfEvalStarted?: boolean;
  /** 評価期間が終了（closed）しているか */
  periodClosed?: boolean;
}

interface StatusDisplay {
  /** 段階名。ファネルの段階名と一致させる */
  stage: string;
  /** 次にアクションする役割。指定時は「（〜待ち）」を付けて表示する */
  nextAction?: string;
  color: StatusColor;
}

/** 自己評価の提出前（目標の承認状態で表示が分かれる段階） */
function isBeforeSelfEvaluationSubmit(status: EvaluationStatus): boolean {
  return status === 'root_defined' || status === 'goal_setting';
}

function resolveStatusDisplay(
  status: EvaluationStatus,
  goalApprovalStatus?: GoalApprovalStatus,
  context?: StatusLabelContext
): StatusDisplay {
  // 評価データが存在しない（一覧の LEFT JOIN で null になるケースを含む）
  if (status === null || status === undefined || status === 'not_started') {
    if (context?.hasRoute === false) {
      return { stage: 'ルート未設定', color: 'default' };
    }
    return { stage: '未着手', color: 'default' };
  }

  if (isBeforeSelfEvaluationSubmit(status)) {
    if (goalApprovalStatus === 'approved') {
      // 自己評価期間の開始前に「待ち」と表示しない
      return context?.selfEvalStarted === false
        ? { stage: '目標確定', color: 'info' }
        : { stage: '自己評価待ち', color: 'info' };
    }
    if (goalApprovalStatus === 'pending_approval') {
      return { stage: '目標承認待ち', color: 'warning' };
    }
    return { stage: '目標設定中', color: 'default' };
  }

  switch (status) {
    case 'self_evaluated':
      // 上長評価無効ルートでは上長評価を行わず、部門長が上長役として直接評価する
      return {
        stage: '自己評価済',
        nextAction: context?.managerEvalDisabled ? '部門長評価' : '上長評価',
        color: 'primary'
      };
    case 'manager_evaluated':
      return { stage: '上長評価済', nextAction: '部門長評価', color: 'secondary' };
    case 'dept_evaluated':
      return { stage: '部門長評価済', nextAction: '会社業績調整', color: 'warning' };
    case 'r_applied':
      return { stage: '会社業績調整済', nextAction: 'HR承認', color: 'success' };
    case 'finalized':
      return { stage: '評価確定', color: 'success' };
    default:
      return { stage: status as string, color: 'default' };
  }
}

/**
 * データベースステータスから日本語ラベルとUIプロパティを取得
 *
 * 表示ルール（設計書 §3.2 の個人表記）:
 * - 評価レコード未作成は「未着手」。評価ルートも無い場合は「ルート未設定」
 * - root_defined / goal_setting はどちらも自己評価の提出前であるため、
 *   目標の承認状態（evaluation_goals.approval_status）で表示を出し分ける
 * - self_evaluated 以降は「（通過済み段階）（（次アクション）待ち）」形式
 * - 期間終了（closed）かつ未確定は「未完了（期間終了）」
 */
export function getStatusLabel(
  status: EvaluationStatus,
  goalApprovalStatus?: GoalApprovalStatus,
  context?: StatusLabelContext
): StatusLabelInfo {
  const progressStep = getProgressStep(status, goalApprovalStatus);

  // 終了した期間に「〜待ち」を残さない
  if (context?.periodClosed && status !== 'finalized') {
    return {
      label: '未完了（期間終了）',
      color: 'default',
      chipColor: 'default',
      progressStep
    };
  }

  const display = resolveStatusDisplay(status, goalApprovalStatus, context);
  const label = display.nextAction ? `${display.stage}（${display.nextAction}待ち）` : display.stage;

  return {
    label,
    color: display.color,
    chipColor: display.color,
    progressStep
  };
}

/**
 * 次アクションを含まない段階名のみを取得する
 *
 * 一覧のフィルタ選択肢など、次アクションまで示すと冗長になる箇所で使う。
 * 返す文字列は組織集計（ファネル）の段階名と同一体系。
 */
export function getStatusStageLabel(
  status: EvaluationStatus,
  goalApprovalStatus?: GoalApprovalStatus,
  context?: StatusLabelContext
): string {
  if (context?.periodClosed && status !== 'finalized') {
    return '未完了（期間終了）';
  }
  return resolveStatusDisplay(status, goalApprovalStatus, context).stage;
}

/**
 * プログレスバー用のステップラベル
 * 段階名は表記マトリクス（設計書 §7）と一致させる
 */
export const PROGRESS_STEPS = [
  '目標設定中',
  '目標承認待ち',
  '目標確定',
  '自己評価済',
  '上長評価済',
  '部門長評価済',
  '会社業績調整済',
  '評価確定',
];

/**
 * 評価進捗バーのアクティブステップ（0-7）
 * evaluations.status と evaluation_goals.approval_status を組み合わせる
 */
export function getProgressStep(
  status: EvaluationStatus,
  goalApprovalStatus?: GoalApprovalStatus | string | null
): number {
  switch (status) {
    case 'self_evaluated':
      return 3;
    case 'manager_evaluated':
      return 4;
    case 'dept_evaluated':
      return 5;
    case 'r_applied':
      return 6;
    case 'finalized':
      return 7;
    default:
      break;
  }

  switch (goalApprovalStatus) {
    case 'pending_approval':
      return 1;
    case 'approved':
      return 2;
    default:
      return 0;
  }
}

/**
 * 組織集計（進捗ファネル）の段階定義
 *
 * evaluations.status のみで決まり、目標の承認状態は参照しない。
 * 段階の合計は母集団の総数と一致する（設計書 §6）。
 */
export type FunnelStageKey =
  | 'not_started'
  | 'goal_setting'
  | 'self_evaluated'
  | 'manager_evaluated'
  | 'dept_evaluated'
  | 'r_applied'
  | 'finalized';

export interface FunnelStageDefinition {
  key: FunnelStageKey;
  /** 段階名（ファネル・滞留サマリで使う） */
  label: string;
  /** 列見出しなど幅が限られる箇所で使う短縮名 */
  shortLabel: string;
  /** 積み上げバー・ファネルの色 */
  color: string;
}

export const EVALUATION_FUNNEL_STAGES: ReadonlyArray<FunnelStageDefinition> = [
  { key: 'not_started', label: '未着手', shortLabel: '未着手', color: '#bdbdbd' },
  { key: 'goal_setting', label: '目標設定', shortLabel: '目標設定', color: '#90caf9' },
  { key: 'self_evaluated', label: '自己評価済', shortLabel: '自己', color: '#42a5f5' },
  { key: 'manager_evaluated', label: '上長評価済', shortLabel: '上長', color: '#1e88e5' },
  { key: 'dept_evaluated', label: '部門長評価済', shortLabel: '部門長', color: '#1976d2' },
  { key: 'r_applied', label: '会社業績調整済', shortLabel: '業績調整', color: '#1565c0' },
  { key: 'finalized', label: '確定済み', shortLabel: '確定', color: '#2e7d32' },
];

/**
 * ファネル段階の人数を取得する
 *
 * root_defined は表示上「目標設定」に合算する。API が root_defined を
 * 分けて返す場合と、すでに合算済みで返す場合の双方に対応する。
 */
export function getFunnelStageCount(
  counts: Partial<Record<string, number>> | null | undefined,
  key: FunnelStageKey
): number {
  if (!counts) return 0;
  const base = Number(counts[key]) || 0;
  if (key === 'goal_setting') {
    return base + (Number(counts.root_defined) || 0);
  }
  return base;
}

/**
 * 一覧画面のステータスフィルタ定義
 *
 * value は API（GET /api/hr/evaluations の status パラメータ）へ送る値。
 * root_defined / goal_setting は表示上ひとつの段階に集約されるため、
 * フィルタは表示段階の単位で用意する。
 */
const STATUS_FILTER_DEFINITIONS: ReadonlyArray<{
  value: string;
  status: EvaluationStatus;
  goalApprovalStatus?: GoalApprovalStatus;
  context?: StatusLabelContext;
}> = [
  { value: 'no_route', status: null, context: { hasRoute: false } },
  { value: 'not_started', status: null },
  { value: 'goal_setting', status: 'goal_setting', goalApprovalStatus: 'draft' },
  { value: 'goal_pending_approval', status: 'goal_setting', goalApprovalStatus: 'pending_approval' },
  { value: 'self_evaluating', status: 'goal_setting', goalApprovalStatus: 'approved' },
  { value: 'self_evaluated', status: 'self_evaluated' },
  { value: 'manager_evaluated', status: 'manager_evaluated' },
  { value: 'dept_evaluated', status: 'dept_evaluated' },
  { value: 'r_applied', status: 'r_applied' },
  { value: 'finalized', status: 'finalized' }
];

/**
 * ステータスフィルタの選択肢（値とラベルの組）
 * ラベルは段階名から導出するため、表示とフィルタが常に一致する。
 */
export const EVALUATION_STATUS_FILTER_OPTIONS: ReadonlyArray<{ value: string; label: string }> =
  STATUS_FILTER_DEFINITIONS.map(definition => ({
    value: definition.value,
    label: getStatusStageLabel(
      definition.status,
      definition.goalApprovalStatus,
      definition.context
    )
  }));

/**
 * 差し戻し可能なステータスかチェック
 * 遷移規則の正本: 設計書 §5.1
 */
export function canReject(
  status: EvaluationStatus,
  userRole: string,
  options?: { managerEvalDisabled?: boolean }
): boolean {
  if (!status) return false;

  // HR: 部門長評価完了 → 上長評価完了（上長評価無効ルートでは自己評価完了）
  if (userRole === 'hr' && status === 'dept_evaluated') {
    return true;
  }

  // HR: 会社業績調整済 → 部門長評価完了（個人単位で会社業績調整をやり直す）
  if (userRole === 'hr' && status === 'r_applied') {
    return true;
  }

  // HR: 評価確定 → 会社業績調整済（HR最終承認の取り消し）
  if (userRole === 'hr' && status === 'finalized') {
    return true;
  }

  // 部門長: 上長評価完了 → 自己評価完了
  if (userRole === 'dept_head' && status === 'manager_evaluated') {
    return true;
  }

  // 部門長（兼務）: 自己評価完了 → 目標設定中
  if (
    userRole === 'dept_head' &&
    options?.managerEvalDisabled &&
    status === 'self_evaluated'
  ) {
    return true;
  }

  // 上長: 自己評価完了 → 目標設定中
  if (userRole === 'manager' && status === 'self_evaluated') {
    return true;
  }

  return false;
}

/**
 * 差し戻し後のステータスを取得
 * 遷移規則の正本: 設計書 §5.1
 */
export function getRejectTargetStatus(
  currentStatus: EvaluationStatus,
  options?: { managerEvalDisabled?: boolean }
): EvaluationStatus {
  // 上長評価無効ルートでは manager_evaluated に戻すと操作できる人がいなくなる（設計書 §5.2）
  if (currentStatus === 'dept_evaluated') {
    return options?.managerEvalDisabled ? 'self_evaluated' : 'manager_evaluated';
  }

  const rejectMap: Record<string, EvaluationStatus> = {
    finalized: 'r_applied',
    r_applied: 'dept_evaluated',
    manager_evaluated: 'self_evaluated',
    self_evaluated: 'goal_setting'
  };

  return rejectMap[currentStatus || ''] || currentStatus;
}

/**
 * ステータスに応じた次のアクション
 */
export function getNextAction(status: EvaluationStatus): string {
  const actionMap: Record<string, string> = {
    not_started: '評価ルートを設定',
    root_defined: '自己評価を入力',
    goal_setting: '自己評価を提出',
    self_evaluated: '上長評価を実施',
    manager_evaluated: '部門長評価を実施',
    dept_evaluated: '会社業績調整（R値適用）を実施',
    r_applied: 'HR承認を実施',
    finalized: '評価完了'
  };

  return actionMap[status || 'not_started'] || '';
}

/**
 * 昇給率を表示できるかチェック
 *
 * @param status - 評価ステータス
 * @param userRole - ユーザーのロール ('hr' | 'employee')
 * @param isEvaluator - 現在のユーザーがこの評価の評価者かどうか（将来の拡張用、現在は未使用）
 * @returns 昇給率を表示できる場合はtrue
 *
 * 表示ルール:
 * - HRは常に表示可能
 * - 評価者・一般社員: ステータスが'finalized'（評価完了）の場合のみ表示可能
 */
export function canViewSalaryRate(
  status: EvaluationStatus,
  userRole: string,
  isEvaluator: boolean = false
): boolean {
  // HRは常に表示可能
  if (userRole === 'hr') {
    return true;
  }

  // 評価者も一般社員も、finalizedの場合のみ表示可能
  return status === 'finalized';
}
